import { request } from "./http";

export interface Entitlements {
  learn: string[];
  practice: string[];
  reference: string[];
}

export interface ContentPack {
  code: string;
  name: string;
  level: string;
  modules: string[];
  price_minor: number;
  description: string;
}

/** A top-up. `price_minor` is both what it costs and what it adds: Rs 500
 *  buys Rs 500 of AI balance, and the markup is taken later, on each call,
 *  at the rate of the model that answered. There is no credit count,
 *  because a count of credits bought a different amount of AI depending on
 *  a model the buyer had not chosen yet. */
export interface CreditPack {
  code: string;
  name: string;
  price_minor: number;
  description: string;
}

export interface Store {
  entitlements: Entitlements;
  content_packs: ContentPack[];
  credit_packs: CreditPack[];
  currency: string;
}

/** The AI wallet. EVERY figure is PAISE — divide by 100 exactly once, at
 *  the point of display, and never anywhere else. */
export interface CreditBalance {
  balance: number;
  granted_this_month: number;
  spent_this_month: number;
  /** What the tenant is granted each month. 0 = nothing recurring. */
  monthly_minor: number;
}

export interface OrderItem {
  code: string;
  name: string;
  price_minor: number;
}

export interface PaymentInfo {
  account_name: string;
  account_number: string;
  bank_name: string;
  ifsc: string;
  contact_email: string;
  note: string;
}

/** Bank-transfer instructions returned when an order is placed. */
export interface OrderInstructions {
  reference: string;
  amount_minor: number;
  currency: string;
  items: OrderItem[];
  payment: PaymentInfo;
}

export function getStore(): Promise<Store> {
  return request("/learning/store");
}

/** Place a pending order and get the bank-transfer instructions (no charge
 *  online — a super-admin unlocks the packs once the transfer arrives). */
export function checkout(packCodes: string[]): Promise<OrderInstructions> {
  return request("/learning/store/checkout", {
    method: "POST",
    body: { pack_codes: packCodes },
  });
}

export function myCredits(): Promise<CreditBalance> {
  return request("/me/ai-credits");
}

// --- super-admin ------------------------------------------------------------
export function grantPack(tenantId: string, packCode: string, reason = ""): Promise<unknown> {
  return request(`/platform/tenants/${tenantId}/grant-pack`, {
    method: "POST",
    body: { pack_code: packCode, reason },
  });
}

export function tenantCredits(tenantId: string): Promise<CreditBalance> {
  return request(`/platform/tenants/${tenantId}/ai-credits`);
}

/** Assign the tenant's monthly allowance, in PAISE. 0 falls back to their
 *  plan. Takes effect at the next monthly grant, never retroactively. */
export function setMonthlyAllowance(
  tenantId: string,
  minor: number,
): Promise<CreditBalance> {
  return request(`/platform/tenants/${tenantId}/ai-credits/monthly`, {
    method: "PUT",
    body: { minor },
  });
}

/** A signed correction in PAISE — refund, goodwill, or a fix. */
export function adjustTenantCredits(
  tenantId: string,
  minor: number,
  reason: string,
): Promise<CreditBalance> {
  return request(`/platform/tenants/${tenantId}/ai-credits/adjust`, {
    method: "POST",
    body: { minor, reason },
  });
}
